// Updated File: src/pages/admin/AdminBanners.jsx
// Changes: Added loading state for submit operations (add/update) to prevent bulk submissions
// - New state: isSubmitting
// - Set isSubmitting(true) in handleSubmit, false in finally block
// - Submit button: disabled during submitting, shows Loader2 spinner, changes text to "Adding..." / "Updating..."
// - Delete remains as-is (single operation, confirm dialog prevents bulk)
// - Ensured all Lucide icons are properly imported and used
// - If error persists, ensure 'lucide-react' is installed: npm i lucide-react

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { themeAPI } from '../../services/api';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Image as ImageIcon, 
  Calendar, 
  Loader2, 
  ArrowLeft, 
  Settings, 
  Filter, 
  Check, 
  X, 
  AlertCircle 
} from 'lucide-react';
import './css/AdminBanners.css'; // Updated CSS import

const MAX_BANNER_BYTES = 750 * 1024;
const MAX_BANNER_WIDTH = 2048;
const MAX_BANNER_HEIGHT = 1152;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Image read failed'));
  reader.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Image load failed'));
  img.src = src;
});

const estimateDataUrlBytes = (dataUrl) => {
  const base64 = String(dataUrl).split(',')[1] || '';
  return Math.ceil((base64.length * 3) / 4);
};

const getOutputMime = () => {
  if (typeof document === 'undefined') return 'image/jpeg';
  const canvas = document.createElement('canvas');
  const test = canvas.toDataURL('image/webp');
  return test.startsWith('data:image/webp') ? 'image/webp' : 'image/jpeg';
};

const compressBannerImage = async (file) => {
  const dataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(dataUrl);
  const scale = Math.min(
    MAX_BANNER_WIDTH / image.width,
    MAX_BANNER_HEIGHT / image.height,
    1
  );
  const targetWidth = Math.max(1, Math.round(image.width * scale));
  const targetHeight = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false });
  const mime = getOutputMime();
  let quality = 0.86;
  let size = Infinity;
  let data = '';
  let downscale = 1;

  const draw = () => {
    canvas.width = Math.max(1, Math.round(targetWidth * downscale));
    canvas.height = Math.max(1, Math.round(targetHeight * downscale));
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  };

  draw();
  data = canvas.toDataURL(mime, quality);
  size = estimateDataUrlBytes(data);

  while (size > MAX_BANNER_BYTES && quality > 0.65) {
    quality -= 0.07;
    data = canvas.toDataURL(mime, quality);
    size = estimateDataUrlBytes(data);
  }

  while (size > MAX_BANNER_BYTES && downscale > 0.7) {
    downscale *= 0.9;
    draw();
    data = canvas.toDataURL(mime, Math.max(0.7, quality));
    size = estimateDataUrlBytes(data);
  }

  return { data, size };
};

const AdminBanners = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false); // New: Loading state for add/update
  const [filterPosition, setFilterPosition] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentBanner, setCurrentBanner] = useState(null);
  const [formData, setFormData] = useState({
    position: 1,
    title: '',
    alt_text: '',
    link: '/products',
    type: 'general',
    expiry_date: '',
    imageBase64: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/login');
      return;
    }
    fetchBanners();
  }, [user, navigate]);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await themeAPI.getBanners();
      if (response.data.success) {
        // Flatten grouped data for table: {1: [...], ...} -> flat array
        const flatBanners = [];
        Object.keys(response.data.data).forEach(pos => {
          response.data.data[pos].forEach(banner => flatBanners.push({ position: parseInt(pos), ...banner }));
        });
        setBanners(flatBanners);
      }
    } catch (err) {
      console.error('Failed to fetch banners:', err);
      alert('Impossible de charger les bannieres');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('La taille de l image doit etre inferieure a 10 Mo');
        return;
      }
      compressBannerImage(file)
        .then(({ data, size }) => {
          if (!data || size > MAX_BANNER_BYTES) {
            alert('Image trop lourde apres compression. Essayez une image plus petite.');
            return;
          }
          setFormData(prev => ({ ...prev, imageBase64: data }));
          setImagePreview(data);
        })
        .catch((err) => {
          console.error('Image compression failed:', err);
          alert("Impossible de preparer l'image. Veuillez reessayer.");
        });
    }
  };

  const handleRemovePreview = () => {
    setFormData(prev => ({ ...prev, imageBase64: '' }));
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Le titre est obligatoire';
    if (modalMode === 'add' && !formData.imageBase64) newErrors.image = 'L image est obligatoire';
    if (formData.position < 1 || formData.position > 5) newErrors.position = 'La position doit etre entre 1 et 5';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      position: 1,
      title: '',
      alt_text: '',
      link: '/products',
      type: 'general',
      expiry_date: '',
      imageBase64: ''
    });
    setImagePreview(null);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true); // New: Start loading

    // Prepare payload: exclude empty imageBase64 for edits
    const payload = { ...formData };
    if (modalMode === 'edit' && !payload.imageBase64) {
      delete payload.imageBase64;
    }

    try {
      let response;
      if (modalMode === 'add') {
        response = await themeAPI.createBanner(payload);
      } else {
        response = await themeAPI.updateBanner(currentBanner.id, payload);
      }
      if (response.data.success) {
        alert(modalMode === 'add' ? 'Banniere ajoutee !' : 'Banniere mise a jour !');
        setShowModal(false);
        resetForm();
        fetchBanners();
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert(err.response?.data?.message || 'Echec de l enregistrement de la banniere');
    } finally {
      setIsSubmitting(false); // New: End loading
    }
  };

  const handleEdit = (banner) => {
    setCurrentBanner(banner);
    setFormData({
      position: banner.position,
      title: banner.title,
      alt_text: banner.alt_text || '',
      link: banner.link,
      type: banner.type,
      expiry_date: banner.expiry_date || '',
      imageBase64: '' // New upload optional
    });
    setImagePreview(banner.image); // Show current image as preview
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Confirmer la suppression de cette banniere ?')) return;
    try {
      const response = await themeAPI.deleteBanner(id);
      if (response.data.success) {
        alert('Banniere supprimee !');
        fetchBanners();
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Echec de suppression de la banniere');
    }
  };

  const filteredBanners = banners.filter(b => filterPosition === 'all' || b.position === parseInt(filterPosition));

  if (loading) {
    return (
      <div className="ab-loading-container">
        <Loader2 className="ab-loading-spinner" />
        <p className="ab-loading-text">Chargement des bannieres...</p>
      </div>
    );
  }

  return (
    <div className="ab-container">
      {/* Header like AdminProducts */}
      <header className="ab-header">
        <button onClick={() => navigate('/admin')} className="ab-back-btn" aria-label="Retour au tableau de bord">
          <ArrowLeft size={20} />
          Retour au tableau de bord
        </button>
        <div className="ab-header-content">
          <div className="ab-header-icon-wrapper">
            <Settings size={24} />
          </div>
          <div className="ab-header-text">
            <h1 className="ab-header-title">Gestion des bannieres</h1>
            <p className="ab-header-subtitle">Gerer les bannieres hero sur 5 positions (jusqu a 10 images chacune)</p>
          </div>
        </div>
        <button 
          onClick={() => { setModalMode('add'); resetForm(); setShowModal(true); }} 
          className="ab-add-btn"
          aria-label="Ajouter une banniere"
        >
          <Plus size={16} />
          <span>Ajouter une banniere</span>
        </button>
      </header>

      {/* Filter like AdminProducts */}
      <div className="ab-filters">
        <div className="ab-filter-wrapper">
          <Filter size={16} />
          <label className="ab-filter-label">Filtrer par position :</label>
          <select 
            value={filterPosition} 
            onChange={(e) => setFilterPosition(e.target.value)}
            className="ab-filter-select"
          >
            <option value="all">Toutes les positions</option>
            {[...Array(5)].map((_, i) => (
              <option key={i+1} value={i+1}>Position {i+1}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Grid like AdminProducts with progress bar */}
      <div className="ab-stats-grid">
        {[...Array(5)].map((_, i) => {
          const count = banners.filter(b => b.position === i+1).length;
          return (
            <div key={i+1} className="ab-stat-card">
              <h3 className="ab-stat-title">Position {i+1}</h3>
              <p className="ab-stat-value">{count}/10</p>
              <div className="ab-stat-bar">
                <div className="ab-stat-fill" style={{ width: `${(count / 10) * 100}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table or Empty State like AdminReviews */}
      {filteredBanners.length === 0 ? (
        <div className="ab-empty-state">
          <ImageIcon className="ab-empty-icon" size={64} />
          <h3 className="ab-empty-title">Aucune banniere trouvee</h3>
          <p className="ab-empty-text">
            {filterPosition === 'all' ? 'Creez votre premiere banniere pour commencer.' : `Aucune banniere en position ${filterPosition}.`}
          </p>
          <button onClick={() => { setModalMode('add'); resetForm(); setShowModal(true); }} className="ab-empty-btn">
            <Plus size={16} />
            Ajouter la premiere banniere
          </button>
        </div>
      ) : (
        <div className="ab-table-wrapper">
          <div className="ab-table-container">
            <table className="ab-table">
              <thead>
                <tr>
                  <th>Position</th>
                  <th>Titre</th>
                  <th>Image</th>
                  <th>Lien</th>
                  <th>Type</th>
                  <th>Actif</th>
                  <th>Expiration</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredBanners.map((banner) => (
                  <tr key={banner.id} className="ab-table-row">
                    <td><span className="ab-badge">{banner.position}</span></td>
                    <td className="ab-title-cell">{banner.title}</td>
                    <td>
                      <div className="ab-preview-wrapper">
                        <img src={banner.image} alt={banner.alt_text} className="ab-preview-img" />
                      </div>
                    </td>
                    <td className="ab-link-cell">{banner.link}</td>
                    <td>
                      <span className={`ab-type-badge ab-type-${banner.type}`}>
                        {banner.type.charAt(0).toUpperCase() + banner.type.slice(1)}
                      </span>
                    </td>
                    <td>
                      <span className={`ab-status-badge ${banner.is_active ? 'ab-active' : 'ab-inactive'}`}>
                        {banner.is_active ? <Check size={12} /> : <X size={12} />}
                        {banner.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td>
                      {banner.expiry_date ? (
                        <div className="ab-expiry-cell">
                          <Calendar size={12} />
                          {banner.expiry_date}
                        </div>
                      ) : (
                        <span className="ab-permanent">Permanent</span>
                      )}
                    </td>
                    <td className="ab-actions-cell">
                      <button onClick={() => handleEdit(banner)} className="ab-action-btn ab-edit-btn" aria-label="Modifier la banniere">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(banner.id)} className="ab-action-btn ab-delete-btn" aria-label="Supprimer la banniere">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal like AdminProducts */}
      {showModal && (
        <div className="ab-modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="ab-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ab-modal-header">
              <h2>{modalMode === 'add' ? 'Ajouter une banniere' : 'Modifier la banniere'}</h2>
              <button 
                onClick={() => { setShowModal(false); resetForm(); }} 
                className="ab-modal-close" 
                aria-label="Fermer la modale"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="ab-form">
              <div className="ab-form-grid">
                <div className="ab-form-group">
                  <label htmlFor="position">Position (1-5) *</label>
                  <select 
                    id="position"
                    name="position" 
                    value={formData.position} 
                    onChange={handleInputChange} 
                    className={errors.position ? 'ab-input-error' : ''}
                  >
                    {[...Array(5)].map((_, i) => (
                      <option key={i+1} value={i+1}>Position {i+1}</option>
                    ))}
                  </select>
                  {errors.position && <span className="ab-error-msg"><AlertCircle size={12} /> {errors.position}</span>}
                </div>

                <div className="ab-form-group">
                  <label htmlFor="title">Titre *</label>
                  <input 
                    id="title"
                    type="text" 
                    name="title" 
                    value={formData.title} 
                    onChange={handleInputChange} 
                    className={errors.title ? 'ab-input-error' : ''}
                    placeholder="Saisir le titre de la banniere"
                    required 
                  />
                  {errors.title && <span className="ab-error-msg"><AlertCircle size={12} /> {errors.title}</span>}
                </div>

                <div className="ab-form-group">
                  <label htmlFor="alt_text">Texte alternatif</label>
                  <input 
                    id="alt_text"
                    type="text" 
                    name="alt_text" 
                    value={formData.alt_text} 
                    onChange={handleInputChange} 
                    placeholder="Texte accessibilite pour l image"
                  />
                </div>

                <div className="ab-form-group">
                  <label htmlFor="link">Lien</label>
                  <input 
                    id="link"
                    type="url" 
                    name="link" 
                    value={formData.link} 
                    onChange={handleInputChange} 
                    placeholder="https://exemple.com ou /produits"
                  />
                </div>

                <div className="ab-form-group">
                  <label htmlFor="type">Type</label>
                  <select id="type" name="type" value={formData.type} onChange={handleInputChange}>
                    <option value="general">General</option>
                    <option value="promo">Promo</option>
                    <option value="featured">Mis en avant</option>
                    <option value="seasonal">Saisonnier</option>
                  </select>
                </div>

                <div className="ab-form-group">
                  <label htmlFor="expiry_date">Date d expiration (optionnel)</label>
                  <input 
                    id="expiry_date"
                    type="date" 
                    name="expiry_date" 
                    value={formData.expiry_date} 
                    onChange={handleInputChange} 
                  />
                </div>

                <div className="ab-form-group ab-full-span">
                  <label htmlFor="image">Image {modalMode === 'edit' ? '(optionnel pour mise a jour)' : '(obligatoire)'} *</label>
                  <input 
                    id="image"
                    type="file" 
                    accept="image/*" 
                    onChange={handleFileChange} 
                    required={modalMode === 'add'}
                    className={errors.image ? 'ab-input-error' : ''}
                  />
                  {errors.image && <span className="ab-error-msg"><AlertCircle size={12} /> {errors.image}</span>}
                  {imagePreview && (
                    <div className="ab-image-preview-wrapper">
                      <img src={imagePreview} alt="Apercu" className="ab-image-preview" />
                      {modalMode === 'add' && (
                        <button type="button" onClick={handleRemovePreview} className="ab-remove-preview-btn">
                          <X size={16} /> Retirer
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="ab-form-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="ab-btn ab-btn-secondary">
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={isSubmitting} // New: Disable during loading
                  className="ab-btn ab-btn-primary"
                >
                  {isSubmitting ? ( // New: Show spinner and loading text
                    <>
                      <Loader2 size={16} className="animate-spin mr-2" />
                      {modalMode === 'add' ? 'Ajout...' : 'Mise a jour...'}
                    </>
                  ) : (
                    modalMode === 'add' ? 'Ajouter la banniere' : 'Mettre a jour la banniere'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
